import { useMemo, useState } from "react";

import { DateRange } from "react-day-picker";

import dayjs from "dayjs";
import { ChevronDown, Download, Loader2 } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useToast } from "~/hooks/use-toast";
import { handleAndNotify } from "~/lib/errorHandler";
import BankFilter from "~/pages/payments/components/BankFilter";
import LatePaymentsTable from "~/pages/payments/components/LatePaymentsTable";
import OutstandingPaymentsTable from "~/pages/payments/components/OutstandingPaymentsTable";
import PaymentMethodDistributionChart from "~/pages/payments/components/PaymentMethodDistributionChart";
import PaymentMethodFilter from "~/pages/payments/components/PaymentMethodFilter";
import PaymentsHistoryTable from "~/pages/payments/components/PaymentsHistoryTable";
import StatsCards from "~/pages/payments/components/StatsCards";
import { usePaymentsData } from "~/pages/payments/hooks/usePaymentsData";
import { usePaymentsSummary } from "~/pages/payments/hooks/usePaymentsSummary";
import DateRangeFilter from "~/pages/purchase-orders/components/filters/DateRangeFilter";
import {
  exportPaymentsForBI,
  exportPaymentsToExcel,
} from "~/services/excelExportService";

// Date preset configurations
const DATE_PRESETS = [
  {
    label: "Hoy",
    getRange: () => ({
      from: dayjs().startOf("day").toDate(),
      to: dayjs().endOf("day").toDate(),
    }),
  },
  {
    label: "Ayer",
    getRange: () => ({
      from: dayjs().subtract(1, "day").startOf("day").toDate(),
      to: dayjs().subtract(1, "day").endOf("day").toDate(),
    }),
  },
  {
    label: "Esta semana",
    getRange: () => ({
      from: dayjs().startOf("week").toDate(),
      to: dayjs().toDate(),
    }),
  },
  {
    label: "Semana anterior",
    getRange: () => ({
      from: dayjs().subtract(1, "week").startOf("week").toDate(),
      to: dayjs().subtract(1, "week").endOf("week").toDate(),
    }),
  },
  {
    label: "Este mes",
    getRange: () => ({
      from: dayjs().startOf("month").toDate(),
      to: dayjs().toDate(),
    }),
  },
  {
    label: "Mes anterior",
    getRange: () => ({
      from: dayjs().subtract(1, "month").startOf("month").toDate(),
      to: dayjs().subtract(1, "month").endOf("month").toDate(),
    }),
  },
  {
    label: "Este año",
    getRange: () => ({
      from: dayjs().startOf("year").toDate(),
      to: dayjs().toDate(),
    }),
  },
  {
    label: "Año anterior",
    getRange: () => ({
      from: dayjs().subtract(1, "year").startOf("year").toDate(),
      to: dayjs().subtract(1, "year").endOf("year").toDate(),
    }),
  },
];

const PaymentsSummaryPage = () => {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf("month").toDate(),
    to: dayjs().toDate(),
  });
  const [activePreset, setActivePreset] = useState<string | null>("Este mes");
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingBI, setExportingBI] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>(
    undefined
  );
  const [bank, setBank] = useState<string | undefined>(undefined);

  // Handle preset button click
  const handlePresetClick = (preset: (typeof DATE_PRESETS)[0]) => {
    const range = preset.getRange();
    setDateRange(range);
    setActivePreset(preset.label);
  };

  // Handle manual date range change (clears preset)
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range || { from: undefined, to: undefined });
    setActivePreset(null);
  };

  const {
    stats,
    latePayments,
    isLoadingLatePayments,
    outstandingPayments,
    isLoadingOutstandingPayments,
    totalLatePayments,
    totalOutstandingPayments,
    mapSeverity, // Using the new mapSeverity function
    hasDateFilter,
  } = usePaymentsSummary(dateRange);

  // Get filtered payments when payment method or bank filter is active
  const { payments: filteredPayments, isLoading: isLoadingFiltered } =
    usePaymentsData(
      dateRange,
      { page: 1, itemsPerPage: 10000 },
      { paymentMethod, bank }
    );

  // Calculate stats from filtered payments when payment method or bank filter is active
  const displayStats = useMemo(() => {
    if ((paymentMethod || bank) && filteredPayments) {
      const filteredTotal = filteredPayments.reduce(
        (sum, payment) => sum + (Number(payment.amount) || 0),
        0
      );
      return {
        total: filteredTotal,
        week: 0,
        month: 0,
        count: filteredPayments.length,
      };
    }
    return {
      total: Number(stats?.total || 0),
      week: Number(stats?.week || 0),
      month: Number(stats?.month || 0),
      count: Number(stats?.count || 0),
    };
  }, [paymentMethod, bank, filteredPayments, stats]);

  // Handle Excel export
  const handleExcelExport = async () => {
    if (!latePayments && !outstandingPayments) {
      toast({
        title: "Sin datos",
        description: "No hay datos de pagos para exportar.",
        variant: "destructive",
      });
      return;
    }

    setExportingExcel(true);
    try {
      await exportPaymentsToExcel(
        [], // Will be fetched inside the export function
        latePayments || [],
        outstandingPayments || [],
        {
          total: Number(stats?.total || 0),
          week: Number(stats?.week || 0),
          month: Number(stats?.month || 0),
          count: Number(stats?.count || 0),
        },
        dateRange
      );
      toast({
        title: "Exportación exitosa",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      handleAndNotify(error, {
        userMessage:
          "No se pudo exportar el archivo Excel. Intente nuevamente.",
        context: "exportToExcel",
        component: "PaymentsSummaryPage",
      });
    } finally {
      setExportingExcel(false);
    }
  };

  // Handle BI export
  const handleBIExport = async () => {
    if (!latePayments && !outstandingPayments) {
      toast({
        title: "Sin datos",
        description: "No hay datos de pagos para exportar.",
        variant: "destructive",
      });
      return;
    }

    setExportingBI(true);
    try {
      await exportPaymentsForBI(
        [], // Will be fetched inside the export function
        latePayments || [],
        outstandingPayments || [],
        dateRange
      );
      toast({
        title: "Exportación exitosa",
        description: "El archivo para BI se ha descargado correctamente.",
      });
    } catch (error) {
      handleAndNotify(error, {
        userMessage:
          "No se pudo exportar el archivo para BI. Intente nuevamente.",
        context: "exportForBI",
        component: "PaymentsSummaryPage",
      });
    } finally {
      setExportingBI(false);
    }
  };

  const isExporting = exportingExcel || exportingBI;

  return (
    <PageContainer>
      <PageHeader
        title="Resumen de Pagos"
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting}
                className="h-9 w-full sm:w-auto"
              >
                {isExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Descargar
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={handleExcelExport}
                disabled={exportingExcel}
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleBIExport} disabled={exportingBI}>
                <Download className="mr-2 h-4 w-4" />
                Descargar para BI
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          {/* Quick Date Presets */}
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={activePreset === preset.label ? "default" : "outline"}
                size="sm"
                onClick={() => handlePresetClick(preset)}
                className="h-9 text-xs"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Date Range + Method + Bank */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center [&_[role=combobox]]:h-9 [&_button]:h-9">
            <div className="w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
              <DateRangeFilter
                dateRange={dateRange}
                setDateRange={handleDateRangeChange}
              />
            </div>
            <div className="w-full sm:w-auto [&>div]:w-full sm:[&>div]:w-auto [&_[role=combobox]]:w-full sm:[&_[role=combobox]]:w-[200px]">
              <PaymentMethodFilter
                value={paymentMethod}
                onValueChange={setPaymentMethod}
              />
            </div>
            <div className="w-full sm:w-auto [&>div]:w-full sm:[&>div]:w-auto [&_[role=combobox]]:w-full sm:[&_[role=combobox]]:w-[220px]">
              <BankFilter value={bank} onValueChange={setBank} />
            </div>
          </div>
        </CardContent>
      </Card>

      <StatsCards
        stats={displayStats}
        isLoading={paymentMethod || bank ? isLoadingFiltered : false}
        hasDateFilter={hasDateFilter}
      />

      <PaymentMethodDistributionChart
        dateRange={dateRange}
        paymentMethod={paymentMethod}
      />

      <div className="space-y-6">
        <PaymentsHistoryTable
          dateRange={dateRange}
          paymentMethod={paymentMethod}
          bank={bank}
        />

        <OutstandingPaymentsTable
          outstandingPayments={outstandingPayments || []}
          totalOutstandingPayments={totalOutstandingPayments}
          isLoading={isLoadingOutstandingPayments}
        />

        <LatePaymentsTable
          latePayments={latePayments}
          totalLatePayments={totalLatePayments}
          isLoading={isLoadingLatePayments}
          getLatenessSeverity={mapSeverity}
          showFullTable={false}
        />
      </div>
    </PageContainer>
  );
};

export default PaymentsSummaryPage;
